import { RainbowKitProvider, darkTheme, lightTheme, Theme } from '@rainbow-me/rainbowkit';
import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';

import { WrongNetworkModal } from '@/app/components/Modal/WrongNetworkModal';
import { config } from '@/app/providers/app/wagmiConfig';
import { commonRoutes } from '@/app/providers/router/routes';
import { useThemeStore } from '@/app/store/theme';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const isIgnorableQueryError = (error: unknown): boolean =>
  error instanceof HTTPError && error.response.status === 404;

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isIgnorableQueryError(error)) return;

      Sentry.captureException(error, {
        contexts: {
          query: {
            queryKey: query.queryKey,
            queryHash: query.queryHash,
          },
        },
      });
    },
  }),

  mutationCache: new MutationCache({
    onError: (error, variables, _, mutation) => {
      Sentry.captureException(error, {
        contexts: {
          mutation: {
            mutationKey: mutation.options.mutationKey,
            variables: variables,
          },
        },
      });
    },
  }),

  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: 300,
    },
  },
});

const DisclaimerComponent = () => (
  <p className='text-[11px] text-gray-400'>
    By connecting a wallet, you agree to the{' '}
    <Link to={commonRoutes.TERM_OF_SERVICE} target='_blank' rel='noopener noreferrer' className='text-brand-1100'>
      Terms of Service
    </Link>
    .
  </p>
);

const rainbowTheme = (mode: 'light' | 'dark'): Theme => {
  const base = mode === 'light' ? lightTheme({ fontStack: 'system' }) : darkTheme({ fontStack: 'system' });

  return {
    ...base,
    colors: {
      ...base.colors,
      modalBackground: mode === 'light' ? '#ffffff' : 'var(--elevated-card-1)',
      profileForeground: mode === 'light' ? '#ffffff' : 'var(--elevated-card-1)',
      modalText: 'var(--foreground)',
      modalTextDim: 'var(--gray-400)',
      modalTextSecondary: 'var(--gray-400)',
      modalBorder: 'var(--decoration-stroke)',
      generalBorder: 'var(--decoration-stroke)',
      closeButton: 'var(--gray-300)',
      closeButtonBackground: 'var(--gray-800)',
      accentColor: 'var(--brand-400)',
      connectButtonText: 'var(--foreground)',
      menuItemBackground: 'var(--elevated-card-hover)',
      profileAction: mode === 'light' ? '#d6e6fc' : '#213752',
      profileActionHover: mode === 'light' ? '#c5d9f4' : '#2a4668',
    },
    radii: {
      ...base.radii,
      connectButton: '6px',
      actionButton: '6px',
      modal: '12px',
    },
    shadows: {
      ...base.shadows,
      connectButton: 'none',
      dialog: 'var(--shadow1)',
    },
  };
};

const RainbowProvider = ({ children }: { children: ReactNode }) => {
  const theme = useThemeStore((state) => state.theme);

  return (
    <RainbowKitProvider
      theme={rainbowTheme(theme)}
      modalSize='compact'
      locale='en'
      appInfo={{
        appName: 'Mezzanine',
        disclaimer: DisclaimerComponent,
      }}
    >
      {children}
      <WrongNetworkModal />
    </RainbowKitProvider>
  );
};

const AppProvider = ({ children }: { children: ReactNode }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowProvider>{children}</RainbowProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default AppProvider;
